// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Sparklight — the screens either side of a round: the menu, the two panels, and the summary.

import { TIERS, TIER_IDS } from './bot';
import { MODES, MODE_IDS } from './modes';
import { fillFor } from './palette';
import { TOP } from './cards';
import type { Summary } from './view';

export const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

export const FOOTER = `
  <footer class="site-footer">
    Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
    · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>
  </footer>`;

export function menuHtml(mode: string, tier: string, crew: number, muted: boolean): string {
  return `
  <div class="main-content menu">
    <h1 class="title">Sparklight</h1>
    <p class="tagline">You can see every hand but your own.</p>

    <div class="setup">
      <fieldset class="opt">
        <legend>Night</legend>
        <div class="segmented" role="radiogroup" aria-label="Mode">
          ${MODE_IDS.map(
            (id) =>
              `<button type="button" role="radio" aria-checked="${id === mode}" class="seg${id === mode ? ' on' : ''}" data-mode="${id}">${esc(MODES[id].name)}</button>`,
          ).join('')}
        </div>
        <p class="opt-blurb" id="modeBlurb">${esc(MODES[mode].blurb)}</p>
      </fieldset>

      <fieldset class="opt">
        <legend>Your crew</legend>
        <div class="segmented" role="radiogroup" aria-label="Crew size">
          ${[2, 3, 4, 5]
            .map(
              (n) =>
                `<button type="button" role="radio" aria-checked="${n === crew}" class="seg${n === crew ? ' on' : ''}" data-crew="${n}">${n}</button>`,
            )
            .join('')}
        </div>
        <div class="segmented" role="radiogroup" aria-label="How good the crew is">
          ${TIER_IDS.map(
            (id) =>
              `<button type="button" role="radio" aria-checked="${id === tier}" class="seg${id === tier ? ' on' : ''}" data-tier="${id}">${esc(TIERS[id].name)}</button>`,
          ).join('')}
        </div>
        <p class="opt-blurb" id="tierBlurb">${esc(TIERS[tier].blurb)}</p>
      </fieldset>
    </div>

    <div class="menu-actions">
      <button type="button" class="primary" id="playSolo">Play</button>
      <button type="button" class="secondary" id="playFriends">Play with friends</button>
    </div>
    <div class="menu-links">
      <button type="button" class="linkish" id="howBtn">How to play</button>
      <button type="button" class="linkish" id="aboutBtn">About</button>
      <button type="button" class="linkish" id="muteBtn" aria-pressed="${muted}">${muted ? 'Sound off' : 'Sound on'}</button>
    </div>
  </div>
  ${FOOTER}`;
}

/**
 * How to play.
 *
 * The hard part of this game is not the rules, it is the CONVENTION — what a hint is supposed to
 * mean. A game of this shape with no stated convention is unplayable for a newcomer: they get
 * pointed at, correctly, and have no idea what they have been asked to do. So the convention is
 * printed here in three sentences, in the same words the crew uses, and the game marks your cards
 * with RAISE and KEEP so you never have to remember which was which.
 */
export const HOW_HTML = `
  <h2>How to play</h2>
  <p>Everyone is building five <b>plumes</b> of light, one colour each, from <b>1 up to 5</b>. You can
  see every hand at the table <b>except your own</b>.</p>
  <ol class="how">
    <li><b>Raise</b> a card onto its plume. If it is the next number that plume wants, the light
    climbs. If not, the sky <b>hazes</b> — three and the night ends.</li>
    <li><b>Douse</b> a card to throw it away and take a <b>spark</b> back.</li>
    <li><b>Point</b> at a crewmate and name one colour or one number. Every card of it in their hand
    lights up. It costs a spark, and you only have eight.</li>
  </ol>
  <h3>What a hint means</h3>
  <p>This is the whole game, and the crew all play it the same way:</p>
  <ul class="how">
    <li>A hint has a <b>focus</b> — the newly-lit card on your <b>chop</b> (the oldest one nobody has
    mentioned) if the hint reached it, otherwise your <b>newest</b> newly-lit card.</li>
    <li>If the focus is on your chop and the hint named a <b>number</b>, it means <b>KEEP IT</b> —
    you were about to throw away something irreplaceable.</li>
    <li><b>Otherwise it means RAISE IT.</b></li>
  </ul>
  <p>Your own cards show what they could still be, and the game writes <b>RAISE</b> or <b>KEEP</b> on
  them for you when the crew has spoken. Swipe a card <b>down</b> to douse it quickly.</p>
  <p class="fine">Beside each plume is a ladder of five marks: <b>▪</b> already raised, <b>○</b>
  spare copies left, <b>◉</b> the last copy — do not douse it, <b>╱</b> all gone, so that plume can
  never get past the mark below. There is no discard pile to search; the ladder is the discard pile.</p>`;

export function aboutHtml(): string {
  return `
  <h2>About</h2>
  <p><b>Sparklight</b> is a co-operative card game about spending a very small amount of speech
  extremely well. It is free, needs no account, and runs entirely in your browser.</p>
  <h3>Playing with friends</h3>
  <p>There is <b>no game server</b>. Your browsers talk to each other directly over WebRTC; a public
  signalling relay only introduces them, and no game state is ever stored anywhere. Connecting to
  someone exchanges IP addresses with them, which is how any direct connection works — so rooms are
  private, and you only ever join one somebody sent you.</p>
  <h3>One honest caveat</h3>
  <p>Somebody has to hold the deck. Whoever that is could, with the browser's developer tools and
  some determination, work out their own hand — because the cards left over are the deck minus
  everything they can already see. There is no way around that without a server, and this game does
  not have one. The player holding the deck is <b>named on screen</b> rather than hidden, and if
  they drop out, whoever takes over is named too.</p>
  <h3>Privacy</h3>
  <p>No accounts, no cookies, no tracking, no third-party fonts or assets. Anonymous, cookie-less
  page-view counts come from Cloudflare Web Analytics, and that is all.</p>
  <p>Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>.
  Card art, sound and every colour on screen are generated in code — there are no asset files.</p>`;
}

export function summaryHtml(s: Summary, sixth: 'prism' | 'onyx' | null, you: number | null): string {
  const pct = Math.round((s.score / s.perfect) * 100);
  const missed = s.missed
    .map(
      (m) =>
        `<li><span class="chip-sm" style="--suit:${fillFor(m.suit, sixth)}">${esc(m.glyph)}</span>
         <b>${esc(m.name)}</b> reached ${m.height} of ${TOP}${
           m.blockedAt ? ` — every ${m.blockedAt} was gone` : ''
         }</li>`,
    )
    .join('');

  return `
  <div class="main-content results">
    <h2 class="res-head">${esc(s.headline)}</h2>
    <p class="res-score"><b>${s.score}</b> <span>of ${s.perfect}</span></p>
    <div class="res-bar"><i style="width:${pct}%"></i></div>

    ${
      s.missed.length
        ? `<h3>What the sky was still missing</h3><ul class="missed">${missed}</ul>`
        : `<h3>Nothing was missing. Every plume full.</h3>`
    }

    <h3>What everyone did</h3>
    <table class="crew">
      <thead><tr><th>Crew</th><th>Raised</th><th>Pointed</th><th>Doused</th><th title="Cards doused that could never be replaced">Lost</th></tr></thead>
      <tbody>
        ${s.crew
          .map(
            (c) => `<tr${c.seat === you ? ' class="me"' : ''}>
            <td>${esc(c.name)}${c.seat === you ? ' <span class="you">you</span>' : ''}</td>
            <td>${c.raised}${c.failed ? ` <span class="fail">+${c.failed} hazed</span>` : ''}</td>
            <td>${c.pointed}</td>
            <td>${c.doused}</td>
            <td>${c.lostForever || '—'}</td>
          </tr>`,
          )
          .join('')}
      </tbody>
    </table>
    <p class="fine">The score is the crew's. The table is who did what, not who did best — in a game
    where only raising scores, a leaderboard would just teach everyone to stop pointing.</p>

    <div class="res-actions">
      <button type="button" class="primary" id="againBtn">Play again</button>
      <button type="button" class="secondary" id="lobbyBtn" hidden>Back to lobby</button>
      <button type="button" class="secondary" id="menuBtn">Menu</button>
      <button type="button" class="linkish results-feedback">Feedback</button>
    </div>
    <p class="waiting" id="resWait" hidden></p>
  </div>
  ${FOOTER}`;
}

/** A panel with all four ways out: the ✕, an outside tap, Escape, and a reachable backdrop. */
export function openPanel(app: HTMLElement, html: string, onClose?: () => void): () => void {
  const wrap = document.createElement('div');
  wrap.className = 'panelwrap';
  wrap.innerHTML = `<div class="backdrop"></div><div class="panel" role="dialog" aria-modal="true">
    <button type="button" class="panel-x" aria-label="Close">✕</button>
    <div class="panel-body">${html}</div></div>`;
  app.append(wrap);

  const panel = wrap.querySelector('.panel') as HTMLElement;
  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('pointerdown', onOutside, true);
    wrap.remove();
    onClose?.();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  const onOutside = (e: Event): void => {
    if (!panel.contains(e.target as Node)) close();
  };
  wrap.querySelector('.panel-x')!.addEventListener('click', close);
  document.addEventListener('keydown', onKey);
  /**
   * Armed late on purpose. The tap that OPENED this panel fires its own trailing `click` after the
   * `pointerup` that ran the handler, and a backdrop listener attached synchronously catches it —
   * so the panel opens and shuts in the same gesture and reads as a dead button.
   */
  setTimeout(() => document.addEventListener('pointerdown', onOutside, true), 350);
  (wrap.querySelector('.panel-x') as HTMLElement).focus();
  return close;
}
